import { useState, useCallback, useRef } from 'react';
const normalize = (state) => {
    const sorted = [...state.filters].sort((a, b) => a.attribute.localeCompare(b.attribute));
    return JSON.stringify({ ...state, filters: sorted });
};
const useSegmentDraft = (initial) => {
    const nextKey = useRef(0);
    const assignKey = (filter) => ({
        ...filter,
        _key: nextKey.current++
    });
    const initialState = useRef({
        name: initial?.name ?? '',
        targetKind: initial?.targetKind ?? '',
        match: initial?.match ?? 'ALL',
        filters: initial?.filters ?? [],
        sourceSegmentId: initial?.sourceSegmentId ?? null
    });
    const defaults = initialState.current;
    const [name, setName] = useState(defaults.name);
    const [targetKind, setTargetKind] = useState(defaults.targetKind);
    const [match, setMatch] = useState(defaults.match);
    const [keyedFilters, setKeyedFilters] = useState(() => defaults.filters.map(assignKey));
    const [sourceSegmentId, setSourceSegmentId] = useState(defaults.sourceSegmentId);
    const filters = keyedFilters.map(({ _key: _, ...rest }) => rest);
    const [savedSnapshot, setSavedSnapshot] = useState(() => normalize(defaults));
    const current = {
        name,
        targetKind,
        match,
        filters,
        sourceSegmentId
    };
    const dirty = normalize(current) !== savedSnapshot;
    const reset = useCallback(() => {
        const original = initialState.current;
        setName(original.name);
        setTargetKind(original.targetKind);
        setMatch(original.match);
        setKeyedFilters(original.filters.map(assignKey));
        setSourceSegmentId(original.sourceSegmentId);
    }, []);
    const commit = useCallback(() => {
        setSavedSnapshot(normalize({ name, targetKind, match, filters, sourceSegmentId }));
    }, [filters, match, name, sourceSegmentId, targetKind]);
    const addFilter = useCallback((filter) => {
        setKeyedFilters((prev) => [
            ...prev,
            assignKey(filter ?? { attribute: '', operator: 'in', values: [] })
        ]);
    }, []);
    const updateFilter = useCallback((index, updated) => {
        setKeyedFilters((prev) => prev.map((f, i) => (i === index ? { ...updated, _key: f._key } : f)));
    }, []);
    const removeFilter = useCallback((index) => {
        setKeyedFilters((prev) => prev.filter((_, i) => i !== index));
    }, []);
    return {
        name,
        setName,
        targetKind,
        setTargetKind,
        match,
        setMatch,
        filters,
        keyedFilters,
        addFilter,
        updateFilter,
        removeFilter,
        setFilters: setKeyedFilters,
        sourceSegmentId,
        setSourceSegmentId,
        dirty,
        reset,
        commit,
        current
    };
};
export default useSegmentDraft;
